import {
  AzureSitesApiBackendClient,
  azureSiteApiRef,
} from '@backstage-community/plugin-azure-sites';
import type { Entity } from '@backstage/catalog-model';
import { compatWrapper } from '@backstage/core-compat-api';
import {
  ApiBlueprint,
  createFrontendModule,
  discoveryApiRef,
  githubAuthApiRef,
  identityApiRef,
} from '@backstage/frontend-plugin-api';
import { ScmAuth, scmAuthApiRef } from '@backstage/integration-react';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';

/**
 * Plugins that have not shipped new-frontend-system (`/alpha`) exports yet.
 * Their React components still work, so they are mounted by hand here with
 * `compatWrapper`, which supplies the legacy APIs they expect. Delete an entry
 * once its package exports `./alpha` — it is auto-discovered from then on.
 *
 * Two things a legacy plugin needs that `compatWrapper` does NOT supply:
 *
 *  - its `createApiFactory` registrations. A legacy plugin declares its API
 *    clients on the plugin object, which the new system never reads, so every
 *    `useApi(thatRef)` throws `NotImplementedError: No implementation available
 *    for apiRef{...}`. They are re-declared as ApiBlueprint extensions below.
 *  - anything an app used to register by hand in `apis.ts`. `ScmAuth` is the one
 *    that bites: `@backstage/integration-react` ships no `/alpha`, so nothing
 *    provides `scmAuthApiRef` until we do.
 *
 * Filters are plain functions rather than the plugins' own `isXAvailable`
 * helpers on purpose: importing those would pull the legacy bundle into the
 * main chunk and defeat the lazy `loader` below.
 */

// azure-sites declares this on its legacy plugin object; mirrored here so the
// card's useApi(azureSiteApiRef) resolves.
const azureSitesApi = ApiBlueprint.make({
  name: 'azure-sites',
  params: defineParams =>
    defineParams({
      api: azureSiteApiRef,
      deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef },
      factory: ({ discoveryApi, identityApi }) =>
        new AzureSitesApiBackendClient({ discoveryApi, identityApi }),
    }),
});

// security-insights reaches GitHub through ScmAuth. GitHub is the only SCM we
// have, so this is `forGithub` rather than the default factory, which would also
// want GitLab, Azure and Bitbucket auth APIs registered.
const scmAuthApi = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams =>
    defineParams({
      api: scmAuthApiRef,
      deps: { githubAuthApi: githubAuthApiRef },
      factory: ({ githubAuthApi }) => ScmAuth.forGithub(githubAuthApi),
    }),
});

const hasAnnotation = (name: string) => (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[name]);

const isAzureSite = hasAnnotation('azure.com/microsoft-web-sites');
const isGithubRepo = hasAnnotation('github.com/project-slug');

// Azure App Service state, with start/stop. Shows on portal, portal-functions
// and melvin-service.
const azureSitesCard = EntityCardBlueprint.make({
  name: 'azure-sites',
  params: {
    filter: isAzureSite,
    loader: () =>
      import('@backstage-community/plugin-azure-sites').then(m =>
        compatWrapper(<m.AzureSitesOverviewWidget />),
      ),
  },
});

// Dependabot alerts, summarised on the overview page.
const dependabotAlertsCard = EntityCardBlueprint.make({
  name: 'dependabot-alerts',
  params: {
    filter: isGithubRepo,
    loader: () =>
      import('@roadiehq/backstage-plugin-security-insights').then(m =>
        compatWrapper(<m.EntityDependabotAlertsCard />),
      ),
  },
});

// The full alert list, as its own entity tab.
//
// This is Dependabot rather than the plugin's code-scanning views
// (EntitySecurityInsightsCard / EntitySecurityInsightsContent). Code scanning on
// a private repo needs GitHub's paid Code Security product — without it the API
// answers 403 "Code Security must be enabled for this repository", so those two
// views could only ever render an error. Swap them back in if that gets bought.
const dependabotContent = EntityContentBlueprint.make({
  name: 'dependabot',
  params: {
    path: 'dependabot',
    title: 'Dependabot',
    filter: isGithubRepo,
    loader: () =>
      import('@roadiehq/backstage-plugin-security-insights').then(m =>
        compatWrapper(<m.EntityGithubDependabotContent />),
      ),
  },
});

export const legacyPluginsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    azureSitesApi,
    scmAuthApi,
    azureSitesCard,
    dependabotAlertsCard,
    dependabotContent,
  ],
});
